import { useEffect, useMemo, useRef, useState } from 'react'
import { Container } from '@mui/material'
import { urlApi } from '../../config'
import useAuth from '../../hooks/useAuth'
import usePermissions from '../../hooks/usePermissions'
import { DEFAULT_LIMIT, COLUMN_CONFIGS } from './constants'
import { CRMFiltersBar } from './CRMFiltersBar'
import { CRMTable } from './CRMTable'
import { SortModal } from './SortModal'
import { ExportModal } from './ExportModal'
import WhatsAppComposer from '../../common/WhatsAppComposer'

export default function CRMUsers() {
  const { getAccessTokenSilently } = useAuth()
  const { hasPermission, hasAnyPermission } = usePermissions()
  const canExport = hasPermission('export-crm')
  const canSendWhatsApp = hasAnyPermission('edit-user', 'channels')
  const [whatsAppUser, setWhatsAppUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [results, setResults] = useState([])
  const [total, setTotal] = useState(0)

  const [role, setRole] = useState('driver')
  const [period, setPeriod] = useState('1m')
  const [status, setStatus] = useState('all')
  const [routeFilter, setRouteFilter] = useState('all')
  const [sortPriority, setSortPriority] = useState([{ key: 'completed', order: 'desc' }])
  const [sortModalOpen, setSortModalOpen] = useState(false)
  const [draftSort, setDraftSort] = useState([])
  const [userId, setUserId] = useState('')
  const [exportModalOpen, setExportModalOpen] = useState(false)
  const [exportLimit, setExportLimit] = useState(1000)
  const [exporting, setExporting] = useState(false)
  const [currency, setCurrency] = useState('UYU')
  const [offset, setOffset] = useState(0)
  const [limit, setLimit] = useState(DEFAULT_LIMIT)

  const cacheRef = useRef(new Map())

  const from = offset + 1
  const to = Math.min(offset + limit, total)

  const fetchData = async () => {
    try {
      setError('')
      const params = new URLSearchParams({
        role,
        period,
        status,
        offset: String(offset),
        limit: String(limit)
      })
      if (userId && userId.trim()) {
        params.set('user_id', userId.trim())
      }
      if (currency) {
        params.set('currency', currency)
      }
      if (routeFilter && routeFilter !== 'all') {
        params.set('route', routeFilter)
      }
      if (sortPriority.length > 0) {
        const keys = sortPriority.map(s => s.key).join(',')
        const orders = sortPriority.map(s => s.order).join(',')
        params.set('sort_by', keys)
        params.set('order', orders)
      }
      const cacheKey = params.toString()
      const cached = cacheRef.current.get(cacheKey)
      if (cached) {
        setResults(cached.results)
        setTotal(cached.total)
        return
      }
      setLoading(true)
      const token = await getAccessTokenSilently()
      const res = await fetch(`${urlApi}/admin/crm?${params.toString()}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(typeof token === 'string' && { 'Authorization': `Bearer ${token}` })
        }
      })
      if (!res.ok) throw new Error('server_error')
      const data = await res.json()
      setResults(data.results || [])
      setTotal(data.total || 0)
      cacheRef.current.set(cacheKey, {
        results: data.results || [],
        total: data.total || 0
      })
    } catch (e) {
      setError('Ocurrió un error al cargar el CRM')
    } finally {
      setLoading(false)
    }
  }

  const sortSignature = useMemo(
    () => sortPriority.map(s => `${s.key}:${s.order}`).join(','),
    [sortPriority]
  )
  useEffect(() => {
    fetchData()
  }, [role, period, status, routeFilter, sortSignature, offset, limit, userId, currency])

  const columnConfig = useMemo(
    () => COLUMN_CONFIGS[role] || COLUMN_CONFIGS.driver,
    [role]
  )
  const sortableOptions = useMemo(
    () => columnConfig.filter(c => c.sortKey).map(c => ({ value: c.sortKey, label: c.label })),
    [columnConfig]
  )

  const openSortModal = () => {
    setDraftSort(sortPriority)
    setSortModalOpen(true)
  }
  const closeSortModal = () => setSortModalOpen(false)

  const addDraftCriterion = () => {
    // elegir el primer campo disponible que no esté usado
    const used = new Set(draftSort.map(d => d.key))
    const nextOpt = sortableOptions.find(o => !used.has(o.value))
    if (!nextOpt) return
    setDraftSort(prev => [...prev, { key: nextOpt.value, order: 'desc' }])
  }
  const updateDraftKey = (index, key) => {
    setDraftSort(prev => {
      const next = [...prev]
      next[index] = { ...next[index], key }
      return next
    })
  }
  const updateDraftOrder = (index, order) => {
    setDraftSort(prev => {
      const next = [...prev]
      next[index] = { ...next[index], order }
      return next
    })
  }
  const removeDraft = index => {
    setDraftSort(prev => prev.filter((_, i) => i !== index))
  }
  const moveDraft = (index, dir) => {
    const target = index + dir
    if (target < 0 || target >= draftSort.length) return
    setDraftSort(prev => {
      const next = [...prev]
      const tmp = next[index]
      next[index] = next[target]
      next[target] = tmp
      return next
    })
  }
  const applyDraftSort = () => {
    // normalizar: quitar vacíos/duplicados y limitar a 3
    const seen = new Set()
    const normalized = []
    for (const item of draftSort) {
      if (!item?.key) continue
      if (seen.has(item.key)) continue
      seen.add(item.key)
      normalized.push({ key: item.key, order: item.order === 'asc' ? 'asc' : 'desc' })
      if (normalized.length >= 3) break
    }
    if (normalized.length === 0) {
      // default si queda vacío
      normalized.push({ key: 'completed', order: 'desc' })
    }
    setSortPriority(normalized)
    setOffset(0)
    setSortModalOpen(false)
  }

  const handlePrev = () => {
    setOffset(Math.max(0, offset - limit))
  }
  const handleNext = () => {
    if (offset + limit < total) setOffset(offset + limit)
  }

  const numberFmt = useMemo(() => new Intl.NumberFormat('es-UY'), [])

  const handleClearFilters = () => {
    setRole('driver')
    setPeriod('1m')
    setStatus('all')
    setRouteFilter('all')
    setUserId('')
    setCurrency('UYU')
    setOffset(0)
  }

  const handleExport = async () => {
    try {
      setExporting(true)
      const params = new URLSearchParams({
        role,
        period,
        status,
        limit: String(Math.min(Math.max(1, exportLimit), 10000))
      })
      if (userId && userId.trim()) {
        params.set('user_id', userId.trim())
      }
      if (currency) {
        params.set('currency', currency)
      }
      if (routeFilter && routeFilter !== 'all') {
        params.set('route', routeFilter)
      }
      if (sortPriority.length > 0) {
        const keys = sortPriority.map(s => s.key).join(',')
        const orders = sortPriority.map(s => s.order).join(',')
        params.set('sort_by', keys)
        params.set('order', orders)
      }
      const token = await getAccessTokenSilently()
      const res = await fetch(`${urlApi}/admin/crm/export?${params.toString()}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          ...(typeof token === 'string' && { 'Authorization': `Bearer ${token}` })
        }
      })
      if (!res.ok) throw new Error('export_error')
      const blob = await res.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `crm_export_${role}_${new Date().toISOString().slice(0, 10)}.csv`
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
      setExportModalOpen(false)
    } catch (e) {
      setError('Ocurrió un error al exportar')
    } finally {
      setExporting(false)
    }
  }

  const displayResults = useMemo(() => {
    if (!Array.isArray(results) || results.length === 0) return results
    if (sortPriority.length <= 1) return results
    const getValue = (obj, key) => (obj ? obj[key] : undefined)
    const comparator = (a, b) => {
      for (const { key, order } of sortPriority) {
        const va = getValue(a, key)
        const vb = getValue(b, key)
        const aNull = va === null || va === undefined
        const bNull = vb === null || vb === undefined
        let cmp = 0
        if (aNull && bNull) {
          cmp = 0
        } else if (aNull) {
          cmp = 1
        } else if (bNull) {
          cmp = -1
        } else if (typeof va === 'number' && typeof vb === 'number') {
          cmp = va - vb
        } else {
          // comparación de strings/otros
          const sa = String(va).toLowerCase()
          const sb = String(vb).toLowerCase()
          cmp = sa.localeCompare(sb, 'es')
        }
        if (cmp !== 0) return order === 'asc' ? cmp : -cmp
      }
      return 0
    }
    const arr = [...results]
    arr.sort(comparator)
    return arr
  }, [results, sortPriority])

  return (
    <Container
      maxWidth={false}
      sx={{
        height: 'calc(100vh - 4rem)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        width: '100%',
        maxWidth: '100%'
      }}
    >
      {/* Filters */}
      <CRMFiltersBar
        currency={currency}
        setCurrency={setCurrency}
        role={role}
        setRole={setRole}
        period={period}
        setPeriod={setPeriod}
        status={status}
        setStatus={setStatus}
        routeFilter={routeFilter}
        setRouteFilter={setRouteFilter}
        userId={userId}
        setUserId={setUserId}
        setOffset={setOffset}
        loading={loading}
        handleClearFilters={handleClearFilters}
        openSortModal={openSortModal}
        setExportModalOpen={canExport ? setExportModalOpen : null}
      />

      {/* Table */}
      <CRMTable
        columnConfig={columnConfig}
        sortPriority={sortPriority}
        loading={loading}
        error={error}
        results={displayResults}
        numberFmt={numberFmt}
        total={total}
        from={from}
        to={to}
        offset={offset}
        limit={limit}
        handlePrev={handlePrev}
        handleNext={handleNext}
        onSendWhatsApp={
          canSendWhatsApp
            ? row =>
                setWhatsAppUser({
                  user_id: row.user_id,
                  first_name: (row.name || '').trim().split(/\s+/)[0],
                  phone_number: row.phone_number
                })
            : null
        }
      />

      <WhatsAppComposer
        open={Boolean(whatsAppUser)}
        onClose={() => setWhatsAppUser(null)}
        user={whatsAppUser}
      />

      {/* Sort Modal */}
      <SortModal
        sortModalOpen={sortModalOpen}
        closeSortModal={closeSortModal}
        draftSort={draftSort}
        sortableOptions={sortableOptions}
        addDraftCriterion={addDraftCriterion}
        updateDraftKey={updateDraftKey}
        updateDraftOrder={updateDraftOrder}
        removeDraft={removeDraft}
        moveDraft={moveDraft}
        applyDraftSort={applyDraftSort}
      />

      {/* Export Modal */}
      <ExportModal
        exportModalOpen={exportModalOpen}
        setExportModalOpen={setExportModalOpen}
        exportLimit={exportLimit}
        setExportLimit={setExportLimit}
        exporting={exporting}
        handleExport={handleExport}
      />
    </Container>
  )
}
